import { useMemo, useState } from "react";
import { UNIVERSE } from "@/lib/universe";

/**
 * 2D SVG map of the universe built from `universe.js`.
 * - Projects each galaxy's (x, z) position onto a plane.
 * - Draws each galaxy as a swirling disc, halo, and label.
 * - Lists systems inline. Clicking a galaxy expands its system list.
 */
export default function UniverseMapSvg() {
  const [openId, setOpenId] = useState("GX-01");

  const galaxies = UNIVERSE.galaxies;

  // Project to SVG space. Universe positions live in [-60..60, -30..30] range.
  const W = 1400, H = 900;
  const PAD = 140;
  const bounds = useMemo(() => {
    const xs = galaxies.map(g => g.position[0]);
    const zs = galaxies.map(g => g.position[2]);
    return {
      minX: Math.min(...xs) - 20, maxX: Math.max(...xs) + 20,
      minZ: Math.min(...zs) - 15, maxZ: Math.max(...zs) + 15,
    };
  }, [galaxies]);

  const proj = (x, z) => {
    const px = PAD + ((x - bounds.minX) / (bounds.maxX - bounds.minX)) * (W - PAD * 2);
    const py = PAD + ((z - bounds.minZ) / (bounds.maxZ - bounds.minZ)) * (H - PAD * 2);
    return [px, py];
  };

  // Deterministic pseudo-random star field
  const stars = useMemo(() => {
    const arr = [];
    let seed = 1337;
    const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    for (let i = 0; i < 220; i++) {
      arr.push({ x: rnd() * W, y: rnd() * H, r: rnd() * 1.2 + 0.2, o: rnd() * 0.7 + 0.15 });
    }
    return arr;
  }, []);

  const opened = galaxies.find(g => g.id === openId);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto max-h-[80vh] block"
      xmlns="http://www.w3.org/2000/svg"
      data-testid="universe-map-svg"
    >
      <defs>
        {galaxies.map((g) => (
          <radialGradient key={`grad-${g.id}`} id={`grad-${g.id}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor={g.color} stopOpacity="0.95" />
            <stop offset="55%" stopColor={g.color} stopOpacity="0.45" />
            <stop offset="100%" stopColor={g.color} stopOpacity="0" />
          </radialGradient>
        ))}
        <radialGradient id="bg-vignette" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#050a18" />
          <stop offset="100%" stopColor="#000000" />
        </radialGradient>
      </defs>

      {/* Background */}
      <rect x="0" y="0" width={W} height={H} fill="url(#bg-vignette)" />

      {/* Stars */}
      {stars.map((s, i) => (
        <circle key={i} cx={s.x} cy={s.y} r={s.r} fill="#ffffff" opacity={s.o} />
      ))}

      {/* Connection lines between galaxies (via Aeterna Nexus at origin) */}
      {galaxies.filter(g => g.id !== "GX-00").map(g => {
        const [x1, y1] = proj(0, 0);
        const [x2, y2] = proj(g.position[0], g.position[2]);
        return (
          <line key={`line-${g.id}`} x1={x1} y1={y1} x2={x2} y2={y2}
            stroke={g.color} strokeWidth="1" strokeDasharray="4 6" opacity="0.35" />
        );
      })}

      {/* Galaxies */}
      {galaxies.map((g) => {
        const [cx, cy] = proj(g.position[0], g.position[2]);
        const isOpen = g.id === openId;
        const radius = 70 + g.systems.length * 3;
        return (
          <g key={g.id}
            style={{ cursor: "pointer" }}
            onClick={() => setOpenId(g.id)}
            data-testid={`map-galaxy-${g.id}`}>
            {/* Halo */}
            <circle cx={cx} cy={cy} r={radius + 20} fill={`url(#grad-${g.id})`} opacity="0.9" />
            {/* Core */}
            <circle cx={cx} cy={cy} r={16} fill={g.color} opacity="0.9">
              <animate attributeName="opacity" values="0.6;1;0.6" dur="3s" repeatCount="indefinite" />
            </circle>
            {/* Outline ring when open */}
            {isOpen && (
              <circle cx={cx} cy={cy} r={radius + 6} fill="none" stroke="#89f7fe" strokeWidth="1.5"
                strokeDasharray="6 4" opacity="0.7">
                <animate attributeName="r" values={`${radius + 4};${radius + 14};${radius + 4}`}
                  dur="4s" repeatCount="indefinite" />
              </circle>
            )}
            {/* ID chip */}
            <g transform={`translate(${cx - 32}, ${cy - radius - 42})`}>
              <rect width="64" height="20" rx="2" fill="#000000" stroke={g.color} strokeOpacity="0.7" strokeWidth="1" />
              <text x="32" y="14" textAnchor="middle" fontFamily="ui-monospace, monospace"
                fontSize="10" fill={g.color} letterSpacing="2">{g.id}</text>
            </g>
            {/* Name */}
            <text x={cx} y={cy - radius - 52} textAnchor="middle"
              fontFamily="ui-monospace, monospace" fontSize="20" fontWeight="900"
              fill="#ffffff" letterSpacing="1.2">
              {g.name.toUpperCase()}
            </text>
            {/* Tag */}
            <text x={cx} y={cy + radius + 26} textAnchor="middle"
              fontFamily="ui-monospace, monospace" fontSize="10"
              fill={g.color} letterSpacing="2" opacity="0.9">
              {g.tag}
            </text>
            {/* System count */}
            <text x={cx} y={cy + radius + 42} textAnchor="middle"
              fontFamily="ui-monospace, monospace" fontSize="9"
              fill="#ffffff" opacity="0.55" letterSpacing="2">
              {g.systems.length} SISTEMA{g.systems.length === 1 ? "" : "S"}
            </text>
          </g>
        );
      })}

      {/* Info panel — anchored bottom-left */}
      {opened && (
        <g transform={`translate(24, ${H - 220})`}>
          <rect width="440" height="196" rx="4"
            fill="#000000" opacity="0.75" stroke={opened.color} strokeOpacity="0.55" strokeWidth="1" />
          <text x="16" y="26" fontFamily="ui-monospace, monospace" fontSize="10"
            fill={opened.color} letterSpacing="3">// {opened.id}</text>
          <text x="16" y="52" fontFamily="ui-monospace, monospace" fontSize="20"
            fontWeight="900" fill="#ffffff" letterSpacing="1">{opened.name.toUpperCase()}</text>
          <text x="16" y="70" fontFamily="ui-monospace, monospace" fontSize="10"
            fill={opened.color} letterSpacing="2">{opened.tag}</text>
          {/* System chips */}
          {opened.systems.slice(0, 12).map((s, i) => {
            const col = i % 3;
            const row = Math.floor(i / 3);
            return (
              <g key={s.id} transform={`translate(${16 + col * 140}, ${88 + row * 24})`}>
                <circle cx="4" cy="8" r="3" fill={s.sun?.color || opened.color} />
                <text x="14" y="12" fontFamily="ui-monospace, monospace" fontSize="10"
                  fill="#ffffff" opacity="0.85">
                  {s.name}
                </text>
                <text x="14" y="22" fontFamily="ui-monospace, monospace" fontSize="8"
                  fill="#ffffff" opacity="0.45">
                  {(s.planets || []).length} planeta{(s.planets || []).length === 1 ? "" : "s"}
                </text>
              </g>
            );
          })}
          {opened.systems.length > 12 && (
            <text x="16" y="188" fontFamily="ui-monospace, monospace" fontSize="9"
              fill="#ffffff" opacity="0.4" letterSpacing="1">
              + {opened.systems.length - 12} outros sistemas
            </text>
          )}
        </g>
      )}

      {/* Legend — top-left */}
      <g transform="translate(24, 24)">
        <text fontFamily="ui-monospace, monospace" fontSize="10" fill="#89f7fe" letterSpacing="3">
          // MAPA OFICIAL DO UNIVERSO $UFO
        </text>
        <text y="18" fontFamily="ui-monospace, monospace" fontSize="9" fill="#ffffff" opacity="0.5" letterSpacing="1">
          Clique numa galáxia para ver seus sistemas
        </text>
      </g>
    </svg>
  );
}
